import { FC, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import DiscardConfirmationModal from "../../common/discard-confirmation-modal/DiscardConfirmationModal";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  getAllReleasesAction,
  createReleaseAction,
  updateReleaseAction,
  ReleaseDto,
} from "../../../redux/actions/customerReleaseSignatureAction";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

const schema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  description: Yup.string().required("Description is required"),
  agreementText: Yup.string().nullable().default(""),
  active: Yup.boolean().default(true),
});

type ReleaseFormData = Yup.InferType<typeof schema>;

export interface ReleaseDrawerProps {
  open: boolean;
  toggleDrawer: () => void;
  populateFor?: ReleaseDto;
}

export const ReleaseDrawer: FC<ReleaseDrawerProps> = ({
  open,
  toggleDrawer,
  populateFor,
}) => {
  const dispatch = useAppDispatch();
  const { createLoading, updateLoading } = useAppSelector((s) => s.release);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const snackBar = useSnackbarContext();

  const { control, handleSubmit, reset, formState: { isDirty } } =
    useForm<ReleaseFormData>({
      resolver: yupResolver(schema),
      defaultValues: { name: "", description: "", agreementText: "", active: true },
    });

  useEffect(() => {
    if (open) {
      reset({
        name: populateFor?.name ?? "",
        description: populateFor?.description ?? "",
        agreementText: populateFor?.agreementText ?? "",
        active: populateFor?.active ?? true,
      });
    }
  }, [open, populateFor, reset]);

  const isLoading = createLoading || updateLoading;

  const handleSave = async (data: ReleaseFormData) => {
    try {
      if (populateFor?.id) {
        await dispatch(
          updateReleaseAction({
            id: populateFor.id,
            dto: {
              description: data.description,
              agreementText: data.agreementText || null,
              active: data.active,
            },
          })
        ).unwrap();
        snackBar.showSnackbar("Release updated successfully!", "success");
      } else {
        await dispatch(
          createReleaseAction({
            name: data.name,
            description: data.description,
            agreementText: data.agreementText || null,
            active: data.active ?? true,
          })
        ).unwrap();
        snackBar.showSnackbar("Release created successfully!", "success");
      }
      reset(data);
      dispatch(getAllReleasesAction());
      toggleDrawer();
    } catch {
      snackBar.showSnackbar("Failed to save release. Please try again.", "error");
    }
  };

  const handleCloseAttempt = () => {
    if (isDirty) {
      setExitConfirmOpen(true);
    } else {
      toggleDrawer();
    }
  };

  return (
    <>
      <DiscardConfirmationModal
        open={exitConfirmOpen}
        onKeepEditing={() => setExitConfirmOpen(false)}
        onDiscard={() => { reset(); setExitConfirmOpen(false); toggleDrawer(); }}
      />
      <CustomDrawer
        isOpen={open}
        toggleDrawer={handleCloseAttempt}
        sx={{ width: "36rem" }}
      >
        <form onSubmit={handleSubmit(handleSave)}>
          <Box
            sx={{
              paddingInline: "1.857rem",
              paddingTop: "2rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.8rem",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "var(--fs-md)",
                color: Colors.TEXT_GRAY_DARK,
              }}
            >
              {populateFor ? "Edit Release" : "Add New Release"}
            </Typography>

            {!populateFor && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                <Typography sx={{ fontSize: "var(--fs-base)", color: Colors.TEXT_GRAY_DARK }}>
                  Name <span style={{ color: "red" }}>*</span>
                </Typography>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <CustomTextBox
                      {...field}
                      error={fieldState.invalid}
                      helperText={fieldState.error?.message}
                      placeholder="e.g. ADDRESS_WAIVER"
                      sx={{ width: "100%", backgroundColor: Colors.WHITE, fontFamily: "monospace" }}
                    />
                  )}
                />
              </Box>
            )}

            {populateFor && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                <Typography sx={{ fontSize: "var(--fs-base)", color: Colors.TEXT_GRAY_DARK }}>
                  Name
                </Typography>
                <Typography
                  sx={{
                    fontSize: "var(--fs-base)",
                    fontFamily: "monospace",
                    color: "#515151",
                    backgroundColor: "#f5f5f5",
                    px: "0.85rem",
                    py: "0.7rem",
                    borderRadius: "0.3rem",
                    border: "1px solid #ddd",
                  }}
                >
                  {populateFor.name}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              <Typography sx={{ fontSize: "var(--fs-base)", color: Colors.TEXT_GRAY_DARK }}>
                Description <span style={{ color: "red" }}>*</span>
              </Typography>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState }) => (
                  <CustomTextBox
                    {...field}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    placeholder="e.g. Address Waiver"
                    sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                  />
                )}
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
              <Typography sx={{ fontSize: "var(--fs-base)", color: Colors.TEXT_GRAY_DARK }}>
                Agreement Text
              </Typography>
              <Controller
                name="agreementText"
                control={control}
                render={({ field, fieldState }) => (
                  <textarea
                    {...field}
                    value={field.value ?? ""}
                    rows={10}
                    placeholder="I hereby agree that..."
                    style={{
                      width: "100%",
                      fontSize: "var(--fs-base)",
                      fontFamily: "inherit",
                      padding: "0.71rem 0.85rem",
                      border: fieldState.invalid ? "1px solid red" : "1px solid #ccc",
                      borderRadius: "0.3rem",
                      resize: "vertical",
                      lineHeight: 1.6,
                      color: "#333",
                      outline: "none",
                    }}
                  />
                )}
              />
              <Typography sx={{ fontSize: "var(--fs-2xs)", color: "#999" }}>
                Shown to the customer on the signature pad before signing.
              </Typography>
            </Box>

            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value ?? true}
                      onChange={(e) => field.onChange(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: "var(--fs-base)", color: Colors.TEXT_GRAY_DARK }}>
                      Active
                    </Typography>
                  }
                />
              )}
            />

            <CustomButton
              sx={{ width: "12rem", height: "2.6rem" }}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : populateFor ? (
                "Save Changes"
              ) : (
                "Create Release"
              )}
            </CustomButton>
          </Box>
        </form>
      </CustomDrawer>
    </>
  );
};
